/* eslint-disable react/jsx-key */
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StyledButtonBack, StyledReactSVG, Text } from "../Pages.style";
import backIcon from "../../assets/images/icons/chevron-left-solid.svg";
import userIcon from "../../assets/images/icons/user-alt.svg";
import { Spin } from "antd";
import {
  Wrapper,
  Container,
  Title,
  Thead,
  Thead2,
  FixedColumn,
  ScrollableWrapper,
  Table,
  Tbody,
  TbodyNames,
  ThHour,
  Td,
  TdName,
  DateHeaderContainer,
  DateNumber,
  WeekdayName,
  BadgeSymbol,
  FooterContainer,
  TextFooter,
  TextFooterVersion,
  FilterSelectInput,
  DaysInMonth,
  BackgroudThead,
} from "./MonthyPlan.style";
import { useAuthStateValue } from "../../context/AuthContext";
import { useMonthlyPlan } from "./hooks/monthlyPlan";
import { ReactSVG } from "react-svg";
import shiftsData from "../../util/shiftTypes.json";

const MonthyPlan = () => {
  const { t } = useTranslation();
  const { loggedUser } = useAuthStateValue();
  const [version, setVersion] = useState("");
  const [data, setData] = useState(null);
  const [changeAtcu, setChangeAtcu] = useState(null);
  const [havePermission, setHavePermission] = useState(false);

  const selectedtMonth = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  const currentMonthName = new Date().getMonth();
  const atcu = loggedUser?.extendUser?.locationIdentificator;

  useEffect(() => {
    const atcu1 = loggedUser?.localPermissions?.[75]?.monthlyPlan;
    const atcu2 = loggedUser?.localPermissions?.[76]?.monthlyPlan;
    if (atcu1 === "NONE" || atcu2 === "NONE") {
      setHavePermission(false);
    } else {
      setHavePermission(true);
    }
  }, [loggedUser?.localPermissions]);

  const {
    fetchMonthlyAll,
    dataSourceMonthlyAll,
    dataSourceMonthlyAllNextMonth,
    dataSourceMonthlyContent,
    dataSourceMonthlyContentNextMonth,
    loading,
  } = useMonthlyPlan({
    atcu: changeAtcu || atcu,
    year,
    selectedtMonth,
  });

  useEffect(() => {
    const locationToUse = changeAtcu || atcu;
    const res = fetchMonthlyAll(locationToUse, year, selectedtMonth);
    setData(res);
  }, [changeAtcu, atcu]);

  useEffect(() => {
    if (dataSourceMonthlyAll) {
      setVersion(dataSourceMonthlyAll?.[0]?.versions?.[0]?.version);
    }
  }, [dataSourceMonthlyAll]);

  const locationChangeHandler = (value) => {
    setChangeAtcu(value);
  };

  let defaultLocation;
  if (atcu != null) {
    defaultLocation = atcu;
  } else {
    defaultLocation = loggedUser?.locationsData[0]?.name;
  }
  const weekdays = ["Pon", "Uto", "Sri", "Čet", "Pet", "Sub", "Ned"];

  const getDaysInMonth = (year, month) => {
    const days = [];
    const lastDay = new Date(year, month, 0).getDate();

    for (let day = 1; day <= lastDay; day++) {
      days.push({
        date: day,
        month: month,
        dayOfWeek: new Date(year, month - 1, day).getDay(),
      });
    }

    return days;
  };

  const monthNames = [
    "Januar",
    "Februar",
    "Mart",
    "April",
    "Maj",
    "Jun",
    "Jul",
    "Avgust",
    "Septembar",
    "Oktobar",
    "Novembar",
    "Decembar",
  ];

  const today = new Date().getDate();

  const optimalWorkingHours = dataSourceMonthlyAll?.[0]?.versions?.[0]?.optimalNumberOfWorkingHours || 0;

  const days = getDaysInMonth(year, selectedtMonth);
  const nextMonthDays =
    today > 15 ? getDaysInMonth(year, selectedtMonth + 1) : [];
  const allDays = [...days, ...nextMonthDays];

  const getSheetData = (
    dataSourceMonthlyContent,
    dataSourceMonthlyContentNextMonth
  ) => {
    const currentMonthControllers = dataSourceMonthlyContent?.controllers || [];
    const nextMonthControllers =
      dataSourceMonthlyContentNextMonth?.controllers || [];

    const controllerMap = new Map();

    // Helper function to get role priority
    const getRolePriority = (role) => {
      switch (role) {
        case "SUPERINTENDENT":
          return 1;
        case "SATCO":
        case "SHIFT_MANAGER":
          return 2;
          case "STUDENT":
            return 4;
        default:
          return 3;
      }
    };

    currentMonthControllers.forEach((controller) => {
      const isShiftManager = controller?.role === "SHIFT_MANAGER";
      const isSatco = controller?.role === "SATCO";
      const isSuperintendent = controller?.role === "SUPERINTENDENT";
      const isStudent = controller?.role === "STUDENT";

      // Combine shifts and absences for current month
      const allShifts = [
        ...(controller?.shifts || []).map((shift) => ({
          ...shift,
          isNextMonth: false,
          type: "shift",
        })),
        ...(controller?.absences || []).map((absence) => ({
          ...absence,
          isNextMonth: false,
          type: "absence",
          shiftName: absence.absenceName,
        })),
      ];

      controllerMap.set(controller.id, {
        key: controller?.id,
        isShiftManager,
        isSatco,
        isSuperintendent,
        isStudent,
        name: `${controller?.firstName} ${controller?.lastName}`,
        shifts: allShifts,
        rolePriority: getRolePriority(controller?.role),
      });
    });

    nextMonthControllers.forEach((controller) => {
      const existingController = controllerMap.get(controller.id);
      if (existingController) {
        // Combine shifts and absences for next month
        const nextMonthShifts = [
          ...(controller?.shifts || []).map((shift) => ({
            ...shift,
            isNextMonth: true,
            type: "shift",
          })),
          ...(controller?.absences || []).map((absence) => ({
            ...absence,
            isNextMonth: true,
            type: "absence",
            shiftName: absence.absenceName,
          })),
        ];

        // Merge with existing shifts
        existingController.shifts = [
          ...existingController.shifts,
          ...nextMonthShifts,
        ];
      } else {
        // If controller doesn't exist in current month, add them
        const isShiftManager = controller?.role === "SHIFT_MANAGER";
        const isSatco = controller?.role === "SATCO";
        const isSuperintendent = controller?.role === "SUPERINTENDENT";
        const isStudent = controller?.role === "STUDENT";

        // Combine shifts and absences for new controller
        const allShifts = [
          ...(controller?.shifts || []).map((shift) => ({
            ...shift,
            isNextMonth: true,
            type: "shift",
          })),
          ...(controller?.absences || []).map((absence) => ({
            ...absence,
            isNextMonth: true,
            type: "absence",
            shiftName: absence.absenceName,
          })),
        ];

        controllerMap.set(controller.id, {
          key: controller?.id,
          isShiftManager,
          isSatco,
          isSuperintendent,
          isStudent,
          name: `${controller?.firstName} ${controller?.lastName}`,
          shifts: allShifts,
          rolePriority: getRolePriority(controller?.role),
        });
      }
    });

    // Convert to array and sort by role priority
    const sortedControllers = Array.from(controllerMap.values()).sort((a, b) => {
      // First sort by role priority
      if (a.rolePriority !== b.rolePriority) {
        return a.rolePriority - b.rolePriority;
      }
      // If same role priority, sort by name
      return a.name.localeCompare(b.name);
    });

    return { controllerRows: sortedControllers };
  };
  const { controllerRows } = getSheetData(
    dataSourceMonthlyContent,
    dataSourceMonthlyContentNextMonth
  );
  const currentUserId = loggedUser?.id;
  return (
    <Container>
      <div className={"loading" + (loading ? " shown" : " not-shown")}>
        <Spin />
      </div>
      <Title>
        <StyledButtonBack>
          <NavLink to="/">
            <StyledReactSVG src={backIcon}></StyledReactSVG>
          </NavLink>
        </StyledButtonBack>
        {havePermission ? (
          <div>
            <Text>{t("main.monthlyPlan")}</Text>
            <FilterSelectInput
              defaultValue={defaultLocation}
              options={loggedUser?.locationsData}
              onChange={locationChangeHandler}
            />
          </div>
        ) : (
          <Text>
            {t("main.monthlyPlan")} - {atcu}
          </Text>
        )}
      </Title>
            <Wrapper havePermission={havePermission}>
        <ScrollableWrapper>
          <Table>
            <thead>
                <tr>
                <DaysInMonth style={{width: '110px', position: 'sticky', left: 0, zIndex: 50}}>Br. sati: {optimalWorkingHours}</DaysInMonth>
                {allDays.map(({ date, dayOfWeek, month }, index) => {
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const isNextMonth = month === selectedtMonth + 1;
                  return (
                    <DaysInMonth
                      key={`day-${month}-${date}`}
                      data-weekend={isWeekend}
                      isNextMonth={isNextMonth}
                      style={{
                        color: isWeekend ? "#C5C5C5" : "#111",
                      }}
                    >
                      <DateHeaderContainer>
                        <DateNumber isNextMonth={isNextMonth}>
                          {isNextMonth ? `${date}.${month}` : date}
                        </DateNumber>
                        <WeekdayName isNextMonth={isNextMonth}>
                          {weekdays[dayOfWeek === 0 ? 6 : dayOfWeek - 1]}
                        </WeekdayName>
                      </DateHeaderContainer>
                    </DaysInMonth>
                  );
                })}
              </tr>
              <BackgroudThead havePermission={havePermission}></BackgroudThead>
            </thead>
            <tbody>
              {controllerRows.map((controller, rowIndex) => (
                <tr
                  key={`row-${rowIndex}`}
                  className={
                    controller.key == currentUserId
                      ? "current-user"
                      : ""
                  }
                >
                  <TdName
                    className={
                      controller.isShiftManager
                      ? "shift-manager"
                      : controller.isSatco
                      ? "satco"
                      : controller.isSuperintendent
                      ? "superintendent"
                      : controller.isStudent
                      ? "student"
                      : ""
                    }
                    style={{width: '110px', position: 'sticky', left: 0, zIndex: 40}}
                  >
                    {controller.name}
                  </TdName>
                  {allDays.map(({ date, dayOfWeek, month }, colIndex) => {
                    const isNextMonth = month === selectedtMonth + 1;
                    const shift = controller.shifts.find(
                      (s) => s.date === date && s.isNextMonth === isNextMonth
                    );

                    const shiftData = shift
                      ? shiftsData.find((s) => s.key === shift.shiftName)
                      : null;

                    return (
                      <Td
                        key={`${rowIndex}-${month}-${date}`}
                        style={{
                          backgroundColor: shiftData?.cellColor || "#ffffff",
                        }}
                        data-weekend={dayOfWeek === 0 || dayOfWeek === 6}
                      >
                        {shift && (
                          <>
                            {shift.shiftName}
                            {(shift.isSatco || shift.isShiftManager || shift.isSuperintendent) && (
                              <BadgeSymbol>
                                <ReactSVG src={userIcon} />
                              </BadgeSymbol>
                            )}
                          </>
                        )}
                      </Td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollableWrapper>
      </Wrapper>
      <FooterContainer>
        <TextFooter
          className={
            dataSourceMonthlyAll?.month && dataSourceMonthlyAll?.year
              ? "currentMonth"
              : ""
          }
        >
          {today > 15
            ? `${monthNames[currentMonthName]} - ${
                monthNames[currentMonthName === 11 ? 0 : currentMonthName + 1]
              } ${year}.`
            : `${monthNames[currentMonthName]} ${year}.`}
        </TextFooter>
      </FooterContainer>
    </Container>
  );
};

export default MonthyPlan;
